import React, { useEffect } from 'react';

import { identify, setBaseProperties } from '@adsk/acs-web-analytics';
import {
  UserProvider,
  SubscriptionsProvider,
  SubscriptionsConstructionProvider,
  useOptionalUser,
} from '@adsk/alloy-provider';
import { LocaleProvider } from '@adsk/alloy-react-locale';
import { WithAlloySuspense } from '@adsk/alloy-react-suspense';
import { Reset } from '@adsk/alloy-react-theme';

import ConnectedTopNavigationOptionalUser from './ConnectedTopNavigationOptionalUser';
import TopNavigationLayout from './TopNavigationLayout';

const AnalyticsInitializer: React.FC = () => {
  const { data: user, loading } = useOptionalUser();

  const notApplicable = 'N/A';

  useEffect(() => {
    if (user && !loading) {
      identify(user.analyticsId, {
        ...user,
      });
    }
  }, [loading, user]);

  useEffect(() => {
    setBaseProperties({
      license: notApplicable,
      tool_id: notApplicable,
      product_id: notApplicable,
      account_id: notApplicable,
      project_id: notApplicable,
    });
  }, []);

  return null;
};

type PublicPageLayoutProps = {
  children: React.ReactNode;
  onLogout?: () => void;
  locale?: string;
  renderAboveLayout?: () => React.ReactNode;
};
/**
 * PublicPageLayout component is the root component to show a page view for both logged in and logged out users.
 * This component should be used for public links, where the user may or may not be logged in.
 * This component **would not redirect** the user to login page if the user is not logged in.
 */
const PublicPageLayout = React.forwardRef<
  HTMLDivElement,
  PublicPageLayoutProps
>(({ children, onLogout, locale, renderAboveLayout }, ref) => {
  return (
    <LocaleProvider locale={locale}>
      <Reset />
      <UserProvider redirectLoggedOutUsers={false}>
        <SubscriptionsProvider>
          <SubscriptionsConstructionProvider>
            <AnalyticsInitializer />
            <TopNavigationLayout
              ref={ref}
              renderTopNavigation={() => (
                <ConnectedTopNavigationOptionalUser onLogout={onLogout} />
              )}
              renderAboveLayout={renderAboveLayout}
            >
              {children}
            </TopNavigationLayout>
          </SubscriptionsConstructionProvider>
        </SubscriptionsProvider>
      </UserProvider>
    </LocaleProvider>
  );
});

PublicPageLayout.displayName = 'PublicPageLayout';

export default WithAlloySuspense(PublicPageLayout);
